import React, { useRef, useState } from "react";
import SEO from "../components/SEO";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";
import VideoContent from "../assets/video/1.mp4";
import "./css/About.css";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { LazyLoadImage } from 'react-lazy-load-image-component';

import {
  faPlay,
  faPause,
  faVolumeUp,
  faVolumeMute,
  faArrowRight,
} from "@fortawesome/free-solid-svg-icons";

const About = () => {
  const videoRef = useRef(null);
  const [isPlaying, setPlaying] = useState(false);
  const [isMuted, setMuted] = useState(false);
  const progressRef = useRef(null);
  const [progress, setProgress] = useState(0);
  const [currentTime, setCurrentTime] = useState("0:00");
  const [duration, setDuration] = useState("0:00");

  // Convert seconds → mm:ss
  const formatTime = (time) => {
    if (isNaN(time)) return "0:00";
    const minutes = Math.floor(time / 60);
    const seconds = Math.floor(time % 60).toString().padStart(2, "0");
    return `${minutes}:${seconds}`;
  };

  // Update progress + time display while playing
  const handleTimeUpdate = () => {
    const video = videoRef.current;
    const percent = (video.currentTime / video.duration) * 100;
    setProgress(percent);
    setCurrentTime(formatTime(video.currentTime));
  };

  // Set duration when video loads
  const handleLoadedMetadata = () => {
    setDuration(formatTime(videoRef.current.duration));
  };

  // Seek when user clicks progress bar
  const handleSeek = (e) => {
    const rect = progressRef.current.getBoundingClientRect();
    const clickX = e.clientX - rect.left;
    const newTime =
      (clickX / rect.width) * videoRef.current.duration;
    videoRef.current.currentTime = newTime;
  };

  const playPauseHandler = () => {
    if (videoRef.current.paused) {
      videoRef.current.play();
      setPlaying(true);
    } else {
      videoRef.current.pause();
      setPlaying(false);
    }
  };

  const toggleMute = () => {
    setMuted(!isMuted);
    videoRef.current.volume = isMuted ? 1 : 0;
  };

  return (
    <>
      <SEO
        title="About Us - Joujo Safaris | Luxury Safari Tours in Kenya & Africa"
        description="Discover Joujo Safaris – experts in luxury safaris, Big Five adventures, cultural tours, and tailor-made travel across Kenya and Africa. Explore unforgettable journeys with us."
        url="https://joujosafaris.com/about"
        image="https://joujosafaris.com/assets/masai-mara.jpg"
        keywords="Kenya safaris, African safari tours, luxury safari Kenya, Big Five adventures, cultural tours Africa, Joujo Safaris, safari holidays"
      />

      {/* ✅ JSON-LD Structured Data for SEO */}
      <script type="application/ld+json">
        {JSON.stringify({
          "@context": "https://schema.org",
          "@type": "TouristAgency",
          "name": "Joujo Safaris",
          "url": "https://joujosafaris.com",
          "logo": "https://joujosafaris.com/assets/logo.png",
          "image": "https://joujosafaris.com/assets/masai-mara.jpg",
          "description":
            "Joujo Safaris offers luxury safari tours, Big Five adventures, and cultural experiences across Kenya and Africa. Discover tailor-made safari holidays with expert guides.",
          "founder": {
            "@type": "Person",
            "name": "Hildah Maina",
            "jobTitle": "CEO / Founder",
          },
          "address": {
            "@type": "Nairobi",
            "addressCountry": "Kenya",
          },
          "areaServed": [
            "Kenya",
            "Tanzania",
            "Uganda",
            "Rwanda",
            "Africa",
          ],
          "sameAs": [
            "https://x.com/SafarisJoujo",
            "https://www.facebook.com/J0ujosafaris/",
            "https://www.instagram.com/joujo_safaris/",
          ],
        })}
      </script>

      <Navbar />

      <section className="about-hero container">
        <div className="about-hero-container">
          <div className="about-text">
            <div className="about-sub-title">
              <h4 className="text-color">How it Started</h4>
              <h5>Our Story, Vision <br />and Values</h5>
            </div>
            <p>
              JOUJO SAFARIS is a subsidiary company under JOIN US
              JOINERS, an organization dedicated to contemporary
              human and wildlife welfare.
              <br />
              <br />
              Joujo Safaris was founded to create awareness and
              connect with communities in a way they understand—
              both as travelers and service providers. The
              subsidiary also generates revenue to support the
              mother organization’s projects.
            </p>
          </div>

          <div className="about-image">
            <div className="video-sec">
              <video
                ref={videoRef}
                autoPlay={false}
                onTimeUpdate={handleTimeUpdate}
                onLoadedMetadata={handleLoadedMetadata}
                controls={false}
                poster={require("../assets/image/woman_boat_trip.png")}
              >
                <source src={VideoContent} type="video/mp4" />
              </video>
              <button
                className="playpause"
                onClick={playPauseHandler}
              >
                <FontAwesomeIcon
                  icon={isPlaying ? faPause : faPlay}
                />
              </button>
              <div className="video-controls control-hidden">
                <div>
                  <button onClick={toggleMute}>
                    <FontAwesomeIcon
                      icon={isMuted ? faVolumeMute : faVolumeUp}
                    />
                  </button>
                </div>
                <div className="time-display">
                  <span>{currentTime}</span>
                </div>
                <div
                  ref={progressRef}
                  className="progress-container"
                  onClick={handleSeek}
                >
                  <div
                    className="progress-bar"
                    style={{ width: `${progress}%` }}
                  ></div>
                </div>
                <div className="time-display">
                  <span>{duration}</span>
                </div>
              </div>
            </div>

            <div className="stats">
              <div className="stat">
                <h2>7+</h2>
                <p>Years of Experience</p>
              </div>
              <div className="stat">
                <h2>3000+</h2>
                <p>Clients Satisfied</p>
              </div>
              <div className="stat">
                <h2>200+</h2>
                <p>Destinations</p>
              </div>
              <div className="stat">
                <h2>2000+</h2>
                <p>Reviews</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      <div className="about-main-title container">
        <h4 className="text-color">Learn More</h4>
        <h5>What We Do</h5>
      </div>

      <section className="about-team container" id="team">
              <div className="about-main-title">
                <h4 className="text-color">Our Team</h4>
                <h5>Our Team</h5>
              </div>
              <div className="about-team-card">
                <div className="about-team-grid">
                  <div className="about-team-img">
                    <LazyLoadImage effect="blur" src={require("../assets/image/hildah_maina_ceo_founder_joujo.jpg")} alt="Hildah Maina" />
                    <h3>Hildah Maina</h3>
                    <h4>CEO / FOUNDER</h4>
                    <p>"In the jungle, the mighty jungle"</p>
                  </div>
                </div>
                <div className="about-team-grid">
                  <div className="about-team-img">
                    <LazyLoadImage effect="blur" src={require("../assets/image/njuguna_kieti_CTO.jpg")} alt="Kieti Njuguna" />
                    <h3>Kieti Njuguna</h3>
                    <h4>Chief Technology Officer</h4>
                    <span
                      style={{
                        backgroundColor: "#000",
                        color: "#fff",
                        fontFamily: "monospace",
                        borderRadius: "4px",
                      }}
                    >
                      &lt;p&gt;Hello World&lt;/p&gt;
                    </span>
                  </div>
                </div>
                <div className="about-team-grid">
                  <div className="about-team-img">
                    <LazyLoadImage effect="blur" src={require("../assets/image/zipporah_tours_brand_ambassador.jpg")} alt="Zipporah Wachira" />
                    <h3>Zipporah Wachira</h3>
                    <h4>Brand Ambassador</h4>
                    <p>"Cruising Down Memory Lane"</p>
                  </div>
                </div>
                <div className="about-team-grid">
                  <div className="about-team-img">
                    <LazyLoadImage effect="blur" src={require("../assets/image/fidelis_maina_joujo_team.jpeg")} alt="Fidelis Maina" />
                    <h3>Fidelis Maina</h3>
                    <h4>Tour Operator</h4>
                    <p>"You deserve better, but someone had to step up"</p>
                  </div>
                </div>
                <div className="about-team-grid">
                  <div className="about-team-img">
                    <LazyLoadImage effect="blur"
                      src={require("../assets/image/eric_thiong'o_driver_joujo_safaris.jpg")}
                      alt="Eric Thiong'o"
                    />
                    <h3>Eric Thiong'o</h3>
                    <h4>Driver</h4>
                    
                      <p>
                      "Cruisin down Memory lane"
                    </p>
                  </div>
                </div>
              </div>
            </section>

      <Footer />
    </>
  );
};

export default About;
